import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { DealCard } from "./deal-card";
import { DealTransformed } from "@/lib/schema/deal-schema";
import { useWorkspaceStore } from "@/store/workspace-store";
import { useDealsViewStore } from "@/store/deals-view-store";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { compareDeals } from "./utils";

interface KanbanViewProps {
  deals: DealTransformed[];
  onStatusChange: (params: { dealId: string; status: string }) => void;
  workspaceId: string;
  onDealDeleted?: () => void;
  onDealArchived?: () => void;
}

export const KanbanView = ({
  deals,
  onStatusChange,
  workspaceId,
  onDealDeleted,
  onDealArchived,
}: KanbanViewProps) => {
  const { activeWorkspace, getStatusColor } = useWorkspaceStore();
  const { sort } = useDealsViewStore();

  const columns = useMemo(
    () => activeWorkspace?.statuses.map((s) => s.title) || [],
    [activeWorkspace?.statuses]
  );

  const statusPriority = useMemo(
    () =>
      columns.reduce(
        (acc, title, i) => ({ ...acc, [title]: i }),
        {} as Record<string, number>
      ),
    [columns]
  );

  // Group deals by status; apply the active sort within each column.
  const groupedDeals = useMemo(
    () =>
      columns.reduce((acc, status) => {
        const inColumn = deals.filter((d) => d.overview.status === status);
        acc[status] = [...inColumn].sort((a, b) =>
          compareDeals(a, b, sort.field, sort.direction, { statusPriority })
        );
        return acc;
      }, {} as Record<string, DealTransformed[]>),
    [columns, deals, sort, statusPriority]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  const updateFades = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeftFade(el.scrollLeft > 0);
    setShowRightFade(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateFades();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateFades, { passive: true });
    const ro = new ResizeObserver(updateFades);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateFades);
      ro.disconnect();
    };
  }, [updateFades, columns.length]);

  // Horizontal auto-scroll during drag. @hello-pangea/dnd's built-in
  // auto-scroller stops at the first scrollable ancestor of each Droppable
  // (the column's overflow-y-auto), so it never reaches the outer
  // overflow-x-auto board. We drive board panning ourselves.
  const dragActiveRef = useRef(false);
  const pointerXRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const pointerCleanupRef = useRef<(() => void) | null>(null);

  const tickAutoScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || !dragActiveRef.current) {
      rafRef.current = null;
      return;
    }
    const rect = el.getBoundingClientRect();
    const EDGE = 80;
    const MAX_SPEED = 20;
    const x = pointerXRef.current;
    let delta = 0;
    if (x > rect.right - EDGE) {
      const ratio = Math.min(1, (x - (rect.right - EDGE)) / EDGE);
      delta = ratio * MAX_SPEED;
    } else if (x < rect.left + EDGE) {
      const ratio = Math.min(1, (rect.left + EDGE - x) / EDGE);
      delta = -ratio * MAX_SPEED;
    }
    if (delta !== 0) el.scrollLeft += delta;
    rafRef.current = requestAnimationFrame(tickAutoScroll);
  }, []);

  const handleDragStart = useCallback(() => {
    dragActiveRef.current = true;
    const onPointerMove = (e: PointerEvent) => {
      pointerXRef.current = e.clientX;
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });
    pointerCleanupRef.current = () =>
      window.removeEventListener("pointermove", onPointerMove);
    rafRef.current = requestAnimationFrame(tickAutoScroll);
  }, [tickAutoScroll]);

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      pointerCleanupRef.current?.();
    };
  }, []);

  const stopAutoScroll = () => {
    dragActiveRef.current = false;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    pointerCleanupRef.current?.();
    pointerCleanupRef.current = null;
  };

  const handleDragEnd = (result: DropResult) => {
    stopAutoScroll();
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Update the deal's status using the actual status title
    onStatusChange({
      dealId: draggableId,
      status: destination.droppableId, // droppableId is now the status title
    });
  };

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="relative h-full">
      <div
        ref={scrollRef}
        className="flex gap-4 p-6 h-full overflow-x-scroll [scrollbar-width:auto] [scrollbar-color:hsl(var(--muted-foreground)/0.5)_hsl(var(--muted)/0.5)] [&::-webkit-scrollbar]:!appearance-none [&::-webkit-scrollbar]:h-2.5 [&::-webkit-scrollbar-track]:bg-muted/50 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/50 hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/70"
      >
        {columns.map((status) => {
          const count = groupedDeals[status]?.length || 0;
          const statusColor = activeWorkspace?.statuses.find(
            (s) => s.title === status
          )?.colour;
          const dotStyle = statusColor?.startsWith("#")
            ? { backgroundColor: statusColor }
            : undefined;
          const dotFallback = !statusColor?.startsWith("#")
            ? getStatusColor?.(status)
            : "";

          return (
          <div
            key={status}
            className="flex flex-col flex-none w-80 h-full bg-card border border-border rounded-md"
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2 min-w-0">
                <span
                  aria-hidden
                  style={dotStyle}
                  className={`h-1.5 w-1.5 rounded-full ${
                    dotStyle ? "" : dotFallback || "bg-muted-foreground/40"
                  }`}
                />
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80 truncate">
                  {status}
                </span>
              </div>
              <span className="font-mono text-xs tabular-nums text-muted-foreground/60">
                {count}
              </span>
            </div>
            <Droppable droppableId={status}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`flex-1 overflow-y-auto p-3 ${
                    snapshot.isDraggingOver ? "bg-muted/50" : ""
                  }`}
                >
                  <div className="space-y-4 min-h-[calc(100%-1rem)]">
                    {groupedDeals[status]?.map((deal, index) => (
                      <Draggable
                        key={deal.id}
                        draggableId={deal.id}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              ...provided.draggableProps.style,
                              opacity: snapshot.isDragging ? 0.8 : 1,
                            }}
                          >
                            <DealCard
                              deal={deal}
                              workspaceId={workspaceId}
                              onDealDeleted={onDealDeleted}
                              onDealArchived={onDealArchived}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {groupedDeals[status]?.length === 0 &&
                      !snapshot.isDraggingOver && (
                        <div className="h-24 border-2 border-dashed border-muted rounded-lg flex items-center justify-center text-muted-foreground">
                          Drop deals here
                        </div>
                      )}
                  </div>
                </div>
              )}
            </Droppable>
          </div>
        );
        })}
      </div>
        {showLeftFade && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent"
          />
        )}
        {showRightFade && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent"
          />
        )}
      </div>
    </DragDropContext>
  );
};
